// src/core/metrics/etw.ts
// Kennzahlen des ETW-Analyzers. Aus src/routes/Eigentumswohnung.tsx verschoben,
// nicht verändert: gleiche Formeln, gleiche Reihenfolge der Operationen. Keine
// React-Abhängigkeit.
//
// Die alte, 3-stufige Score-/Entscheidungslogik (scoreRaw/scorePct/decisionLabel,
// RENTABEL/GRENZWERTIG/NICHT_RENTABEL) ist hier bewusst NICHT mehr enthalten --
// ersetzt durch den analyzer-uebergreifenden Deal-Score v2 (src/core/score/dealScore.ts,
// siehe computeEtwDealScoreFromMetrics() in etwDealScore.ts). Entfernt 10.10.2026,
// nachdem Deal-Score v2 fuer ETW zum Standard wurde (siehe DEAL-FLOW-STATUS.md).
//
// Einheitliche Darlehensregel (16.10.2026, Dominiks Vorgabe, letzter der 4 Typen):
// vorher Kredit = All-in x LTV (einziger der 5 Typen, der auf All-in statt Kaufpreis
// basierte -- finanzierte dadurch implizit auch die Nebenkosten). Jetzt wie MFH/EFH/
// Gewerbe/Gemischt: Kredit = Kaufpreis − max(0, Eigenkapital − Nebenkosten), ueber die
// gemeinsame Funktion core/financing.ts.
import { computeFinancing, eigenkapitalFromLegacyLoan } from "../financing.js";

export type EtwMetricsInput = {
  kaufpreis: number;
  flaecheM2: number;
  mieteProM2Monat: number;
  leerstandPct: number;
  opexPctBrutto: number;
  instandhaltungPct: number;
  // Kaufnebenkosten (Anteile, 0..1)
  nkGrEStPct: number;
  nkNotarPct: number;
  nkGrundbuchPct: number;
  nkMaklerPct: number;
  nkSonstPct: number;
  // einmalige Nebenkosten in €
  nkRenovierung: number;
  nkSanierung: number;
  // Finanzierung
  financingOn: boolean;
  /** Vereinheitlichte Darlehensregel (16.10.2026), wie MFH/EFH/Gewerbe/Gemischt:
   *  Eigenkapital in Euro statt Beleihungsquote (LTV). */
  eigenkapital: number;
  zinsPct: number;
  tilgungPct: number;
  // Annahmen
  priceAdjPct: number;
  rentAdjPct: number;
  applyAdjustments: boolean;
  capRatePct: number;
  /** Bewirtschaftung (Baustein 5): ETW behaelt sein Hausgeld+Instandhaltungspct-Modell
   *  UNVERAENDERT bei (kein Pauschal/Einzeln-Umschalter, § 28 II. BV gilt nicht -- die
   *  Instandhaltung des Gemeinschaftseigentums steckt bereits im Hausgeld). Optionales,
   *  zusaetzliches Feld fuer die Ruecklage am SONDEREIGENTUM (die eigene Wohnung, nicht das
   *  Gemeinschaftseigentum) -- Default 0, additiv zur NOI, aendert sonst nichts. */
  sondereigentumRuecklage?: number;
  /** Stress-Pass-Fix (18.10.2026, Dominiks Vorgabe): Hausgeld-Anteil (opexPctBrutto) und
   *  Instandhaltungs-Ruecklage (instandhaltungPct) werden auf der BASIS-Kaltmiete (vor
   *  Mietstress) ermittelt und bleiben unter Mietstress fix -- wie bei den anderen 4 Typen
   *  (siehe MfhMetricsInput.pauschaleBasisMiete). Der Aufrufer setzt dieses Feld nur fuer
   *  den Stress-Durchlauf (auf grossRentYear des Basis-Durchlaufs); bleibt es undefined,
   *  faellt beides auf das eigene grossRentYear zurueck -- identisches Ergebnis wie bisher. */
  pauschaleBasisGrossRentYear?: number;
};

export type EtwMetrics = {
  kaufpreisView: number;
  nkPct: number;
  nkSumPct: number;
  nkSum: number;
  allIn: number;
  mieteProM2Eff: number;
  grossRentYear: number;
  effRentYear: number;
  opexYear: number;
  instandhaltungYear: number;
  /** Bewirtschaftung (Baustein 5): nur > 0, wenn das optionale Sondereigentum-Ruecklage-
   *  Feld gesetzt ist -- additiv, aendert sonst nichts am bisherigen Hausgeld-Modell. */
  sondereigentumRuecklageYear: number;
  noi: number;
  loan: number;
  /** Bedarf (All-in) minus EK minus Kredit; 0, wenn das Eigenkapital die Nebenkosten deckt. */
  finanzierungsluecke: number;
  annuitaetJahr: number;
  annuitaetMonat: number;
  zinsMonat: number;
  tilgungMonat: number;
  noiYield: number;
  dscr: number | null;
  monthlyEffRent: number;
  monthlyOpex: number;
  monthlyInstandhaltung: number;
  monthlySondereigentumRuecklage: number;
  monthlyCF: number;
  wertNOI: number;
  /** ACHTUNG: tatsaechlich eingesetztes Eigenkapital (All-in minus Kredit), NICHT identisch
   *  mit dem Eingabewert `EtwMetricsInput.eigenkapital` -- weicht davon ab, sobald eine
   *  Finanzierungsluecke besteht (dann kleiner als der Eingabewert, s. finanzierungsluecke
   *  oben). Fuer den ETF-Vergleich ("Schlaegt diese Wohnung eine ETF-Anlage?") gedacht. */
  eigenkapital: number;
};

export function computeEtwMetrics(input: EtwMetricsInput): EtwMetrics {
  const {
    kaufpreis, flaecheM2, mieteProM2Monat, leerstandPct, opexPctBrutto, instandhaltungPct,
    nkGrEStPct, nkNotarPct, nkGrundbuchPct, nkMaklerPct, nkSonstPct,
    nkRenovierung, nkSanierung, financingOn, zinsPct, tilgungPct,
    priceAdjPct, rentAdjPct, applyAdjustments, capRatePct,
  } = input;

  const kaufpreisAdj = kaufpreis * (1 + (applyAdjustments ? priceAdjPct : 0));
  const kaufpreisView = kaufpreisAdj;

  const nkPct = nkGrEStPct + nkNotarPct + nkGrundbuchPct + nkMaklerPct + nkSonstPct;
  const nkSumPct = kaufpreisView * nkPct;
  const nkSum = nkSumPct + nkRenovierung + nkSanierung;
  const allIn = kaufpreisView + nkSum;

  const mieteProM2Eff = mieteProM2Monat * (1 + (applyAdjustments ? rentAdjPct : 0));

  const grossRentYear = flaecheM2 * mieteProM2Eff * 12;
  const effRentYear = grossRentYear * (1 - leerstandPct);
  const pauschaleBasisGrossRentYear = input.pauschaleBasisGrossRentYear ?? grossRentYear;
  const opexYear = pauschaleBasisGrossRentYear * opexPctBrutto;
  // Instandhaltungsruecklage: additiv zu opexYear, gleiches Muster wie MFH/EFH/Gewerbe
  // (eigene Ruecklage, nicht in der allgemeinen Bewirtschaftungsquote enthalten).
  const instandhaltungYear = pauschaleBasisGrossRentYear * instandhaltungPct;
  const sondereigentumRuecklageYear = Math.max(0, input.sondereigentumRuecklage ?? 0);
  const noi = Math.max(0, effRentYear - opexYear - instandhaltungYear - sondereigentumRuecklageYear); // vor Finanzierung

  const financing = computeFinancing({ kaufpreis: kaufpreisView, nebenkosten: nkSum, eigenkapital: input.eigenkapital, financingOn });
  const loan = financing.loan;
  const finanzierungsluecke = financing.finanzierungsluecke;
  const annuitaetJahr = financingOn && loan > 0 ? loan * (zinsPct + tilgungPct) : 0;
  const annuitaetMonat = annuitaetJahr / 12;
  const zinsMonat = financingOn ? (loan * zinsPct) / 12 : 0;
  const tilgungMonat = financingOn ? (loan * tilgungPct) / 12 : 0;

  const noiYield = allIn > 0 ? noi / allIn : 0;
  // Ohne Darlehen (Barkauf): dscr = null, nicht Infinity
  const dscr = annuitaetJahr > 0 ? noi / annuitaetJahr : null;

  const monthlyEffRent = effRentYear / 12;
  const monthlyOpex = opexYear / 12;
  const monthlyInstandhaltung = instandhaltungYear / 12;
  const monthlySondereigentumRuecklage = sondereigentumRuecklageYear / 12;
  const monthlyCF = monthlyEffRent - monthlyOpex - monthlyInstandhaltung - monthlySondereigentumRuecklage - annuitaetMonat;

  const wertNOI = capRatePct > 0 ? noi / capRatePct : 0;

  // "Schlägt diese Wohnung eine ETF-Anlage?" -- Eigenkapital
  const eigenkapital = Math.max(0, allIn - loan);

  return {
    kaufpreisView, nkPct, nkSumPct, nkSum, allIn, mieteProM2Eff, grossRentYear, effRentYear, opexYear,
    instandhaltungYear, sondereigentumRuecklageYear, noi, loan, finanzierungsluecke, annuitaetJahr, annuitaetMonat, zinsMonat, tilgungMonat, noiYield, dscr,
    monthlyEffRent, monthlyOpex, monthlyInstandhaltung, monthlySondereigentumRuecklage, monthlyCF, wertNOI, eigenkapital,
  };
}

/** Break-even-Kaufpreis/-Miete (Dominiks Vorgabe, Baustein 4, Bankbericht aus der Akte):
 *  aus Eigentumswohnung.tsx extrahiert (`breakEvenPriceForCashflowZero`/
 *  `breakEvenRentPerM2ForCashflowZero`), urspruenglich fuer Feld-fuer-Feld-Paritaet zum
 *  PDF uebernommen, nicht den generischen Akte-Solver (dealScoreBreakeven.ts). Bugfix
 *  16.10.2026 (einheitliche Darlehensregel): `kaufpreis` war hier bisher laut Aufrufer
 *  tatsaechlich `allIn` (inkl. Nebenkosten) -- eine seit jeher bekannte Ungenauigkeit
 *  (siehe Git-Historie). Die neue, EK-basierte Regel braucht den ECHTEN Kaufpreis
 *  getrennt von den (jetzt variablen) Nebenkosten, deshalb jetzt `kaufpreis` = echter
 *  Kaufpreis + eigenes `nkPct` -- behebt die alte Ungenauigkeit gleich mit. */
export type EtwBreakEvenInput = {
  kaufpreis: number;
  nkPct: number;
  flaecheM2: number;
  mieteProM2Monat: number;
  leerstandPct: number;
  opexPctBrutto: number;
  instandhaltungPct: number;
  financingOn: boolean;
  eigenkapital: number;
  zinsPct: number;
  tilgungPct: number;
  /** Bewirtschaftung (Baustein 5): optionales, festes €/Jahr fuer die Sondereigentum-
   *  Ruecklage -- skaliert NICHT mit Preis/Miete (anders als opexPctBrutto/instandhaltungPct). */
  sondereigentumRuecklage?: number;
};

export function breakEvenPriceForCashflowZero(base: EtwBreakEvenInput): number | null {
  if (!base.financingOn || base.zinsPct + base.tilgungPct <= 0) return null;
  // Nebenkosten skalieren mit dem (hier variierenden) Kaufpreis -- je Iteration ueber
  // computeFinancing() neu bestimmt, statt eines fixen Beleihungs-Multiplikators.
  const cfAt = (price: number) => {
    const gross = base.flaecheM2 * base.mieteProM2Monat * 12;
    const eff = gross * (1 - base.leerstandPct);
    const opex = gross * (base.opexPctBrutto + (base.instandhaltungPct ?? 0)) + Math.max(0, base.sondereigentumRuecklage ?? 0);
    const nebenkosten = Math.round(price * base.nkPct);
    const loan = computeFinancing({ kaufpreis: price, nebenkosten, eigenkapital: base.eigenkapital, financingOn: true }).loan;
    const annu = loan * (base.zinsPct + base.tilgungPct);
    return (eff - opex - annu) / 12;
  };
  let low = 0, high = Math.max(1, base.kaufpreis), cfH = cfAt(high), safe = 0;
  while (cfH > 0 && high < base.kaufpreis * 100 && safe < 40) {
    high *= 1.5;
    cfH = cfAt(high);
    safe++;
  }
  if (cfH > 0) return Math.round(high);
  for (let k = 0; k < 40; k++) {
    const mid = (low + high) / 2, cf = cfAt(mid);
    if (cf >= 0) low = mid;
    else high = mid;
  }
  return Math.round((low + high) / 2);
}

export function breakEvenRentPerM2ForCashflowZero(base: EtwBreakEvenInput): number {
  if (!base.financingOn || base.zinsPct + base.tilgungPct <= 0) return 0;
  // Kaufpreis bleibt hier fix (nur die Miete variiert) -- Kredit einmal vorab bestimmt.
  const nebenkosten = Math.round(base.kaufpreis * base.nkPct);
  const loan = computeFinancing({ kaufpreis: base.kaufpreis, nebenkosten, eigenkapital: base.eigenkapital, financingOn: true }).loan;
  const cfAt = (rent: number) => {
    const gross = base.flaecheM2 * rent * 12;
    const eff = gross * (1 - base.leerstandPct);
    const opex = base.flaecheM2 * base.mieteProM2Monat * 12 * (base.opexPctBrutto + (base.instandhaltungPct ?? 0)) + Math.max(0, base.sondereigentumRuecklage ?? 0);
    const annu = loan * (base.zinsPct + base.tilgungPct);
    return (eff - opex - annu) / 12;
  };
  let low = 0, high = Math.max(0.1, base.mieteProM2Monat), cfH = cfAt(high), safe = 0;
  while (cfH < 0 && high < 200 && safe < 60) {
    high *= 1.2;
    cfH = cfAt(high);
    safe++;
  }
  for (let k = 0; k < 40; k++) {
    const mid = (low + high) / 2, cf = cfAt(mid);
    if (cf >= 0) high = mid;
    else low = mid;
  }
  return Math.round(((low + high) / 2) * 100) / 100;
}
